import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  AbstractControl,
  ValidationErrors,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
} from '@angular/forms';
import { ValidatorService } from '../../../../services/validator.service';
import { AppConfigService } from '@app/core/services/config.service';
import { CountryISO } from 'ngx-intl-tel-input-gg';

@Component({
    selector: 'c-my-emergency-contact',
    templateUrl: './my-emergency-contact.component.html',
    styleUrls: ['./my-emergency-contact.component.scss'],
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => MyEmergencyContactComponent),
        multi: true,
      },
      {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => MyEmergencyContactComponent),
        multi: true,
      },
    ],
    standalone: false
})
export class MyEmergencyContactComponent implements OnInit, ControlValueAccessor {
  fb: FormGroup | undefined;
  appConfigData: any[];
  firstNameControl: FormControl | undefined;
  phoneNumberControl: FormControl | undefined;
  lastNameControl: FormControl | undefined;

  @Input() name: string;
  @Input() stepNames: any[];
  @Input() loading: boolean;
  @Input() genders: any[];
  @Input() nationalities: any[];
  @Input() languages: any[];
  @Input() set reset(value: boolean) {
    if (value === true) {
      this.fb?.reset();
    }
  }
  @Input() preferredCountries: CountryISO[];

  @Output() submitForm: EventEmitter<any> = new EventEmitter();
  @Output() clickEditButton: EventEmitter<any> = new EventEmitter();

  constructor(
    private formBuilder: FormBuilder,
    private validatorService: ValidatorService,
    public appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    this.firstNameControl = new FormControl(null);
    this.phoneNumberControl = new FormControl(null);
    this.lastNameControl = new FormControl(null);

    this.fb = this.formBuilder.group(
      {
        firstName: this.firstNameControl,
        phoneNumber: this.phoneNumberControl,
        lastName: this.lastNameControl,
      },
      { validators: [this.validateForm.bind(this)] }
    );
  }

  /**
   * Validate form
   */
  private validateForm() {
    if (this.firstNameControl) {
      this.validatorService.setValidator(
        this.firstNameControl,
        this.validatorService.validateOn({
          required: true,
        })
      );
    }

    if (this.phoneNumberControl) {
      this.validatorService.setValidator(
        this.phoneNumberControl,
        this.validatorService.validateOn({
          phoneValid: true,
        })
      );
    }

    if (this.lastNameControl) {
      this.validatorService.setValidator(
        this.lastNameControl,
        this.validatorService.validateOn({
          required: true,
        })
      );
    }
  }

  /**
   * Submit form
   */
  submit() {
    if (this.fb?.valid) {
      this.submitForm.emit(this.fb.value);
    } else {
      this.validatorService.validateAllFormFields(this.fb);
    }
  }

  /**
   * ControlValueAccessor functions
   */
  public onTouched: () => void = () => {};

  writeValue(val: any): void {
    val && this.fb?.setValue(val, { emitEvent: false });
  }
  registerOnChange(fn: any): void {
    this.fb?.valueChanges.subscribe(fn);
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.fb.disable() : this.fb.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.fb?.valid
      ? null
      : {
          invalidForm: {
            valid: false,
            message: `${this.name} fields are invalid`,
          },
        };
  }
}
